import { AuthApiError } from '@supabase/supabase-js';

import { getErrorMessage } from '@/lib/supabase-utils';
import { isDesktopPlatform } from '@/lib/desktop/bridge';

import { isNativePlatform } from './platform';

import type { QueryClient } from '@tanstack/react-query';
import type { AuthError, SupabaseClient } from '@supabase/supabase-js';

/**
 * Window event dispatched after a successful native or desktop resume so the realtime
 * channel hook can tear down and re-subscribe on a fresh JWT. Fired only on
 * suspended app can rejoin with a fresh JWT; normal web ignores it.
 */
export const NATIVE_RESUME_EVENT = 'pp:native-resume';

/** Suppress a second resume within this window of the last one (rapid fg/bg flaps). */
const RESUME_DEBOUNCE_MS = 5000;
/** Skip resume within this window of process start - cold boot already primes everything. */
const COLD_START_GRACE_MS = 30_000;
/**
 * Refresh on resume only within this window of access-token expiry - including
 * already-expired. Every refresh rotates the refresh token, and each rotation
 * is a chance for the lost-response reuse-detection race that revokes the
 * whole session family; a fresh token has nothing to gain from the gamble.
 * 120s sits deliberately above auth-js's own 90s EXPIRY_MARGIN_MS so the two
 * schedulers never disagree about "fresh", and doubles as the device
 * clock-skew allowance.
 */
const REFRESH_EXPIRY_MARGIN_MS = 120_000;

/**
 * Dependencies injected into {@link runNativeResume}. The I/O the pipeline
 * performs - session refresh, realtime re-prime/rejoin, query invalidation, and
 * the clock - is injected so those need no module mocking in tests. The platform
 * guard (`isNativePlatform`) stays a direct import; tests stub `./platform`.
 */
export interface RunNativeResumeDeps {
  readonly supabase: Pick<SupabaseClient, 'auth'>;
  readonly queryClient: Pick<QueryClient, 'invalidateQueries'>;
  readonly applyRealtimeAuthToken: (accessToken: string | null | undefined) => Promise<void>;
  /** Dispatch the realtime-rejoin signal (a {@link NATIVE_RESUME_EVENT} window event). */
  readonly dispatchRealtimeRejoin: () => void;
  readonly isAuthCircuitOpen: () => boolean;
  /** Trip the auth circuit breaker (the extracted `handleAuthCircuitBreak`). */
  readonly tripCircuit: () => void;
  /** Monotonic clock in ms (injectable for tests). Defaults to `Date.now`. */
  readonly now?: () => number;
}

export type RunAppResumeDeps = RunNativeResumeDeps;

interface RunAppResumeOptions {
  readonly allowDuringColdStart?: boolean;
  readonly allowDuringDebounce?: boolean;
  readonly retryAfterActiveTransientFailure?: boolean;
}

const RESUME_RUN_COMPLETED = 'completed';
const RESUME_RUN_NOT_RECOVERED = 'not-recovered';
const RESUME_RUN_RETRYABLE_FAILURE = 'retryable-failure';

type ResumeRunOutcome =
  | typeof RESUME_RUN_COMPLETED
  | typeof RESUME_RUN_NOT_RECOVERED
  | typeof RESUME_RUN_RETRYABLE_FAILURE;

interface ActiveResumeRun {
  promise: Promise<ResumeRunOutcome>;
}

// Module-scoped run bookkeeping. `processStartMs` is stamped at import time so
// the cold-start grace window measures from app boot, not from the first resume.
let processStartMs = Date.now();
let lastRunMs = 0;
let activeResumeRun: ActiveResumeRun | null = null;

/**
 * Reset run bookkeeping. Test-only. `processStartAt` re-stamps the cold-start
 * baseline so a test's injected `now()` clock shares the same scale.
 */
export function __resetResumePipelineForTests(processStartAt = Date.now()): void {
  processStartMs = processStartAt;
  lastRunMs = 0;
  activeResumeRun = null;
}

/**
 * Error codes from a failed `refreshSession()` that mean the session is dead
 * and the user must re-auth. `user_banned` is here deliberately: suspension
 * bans the user, a ban blocks refresh with that code, and a classifier
 * missing it leaves a suspended staffer's device signed in forever.
 */
const TERMINAL_AUTH_REFRESH_CODES = new Set([
  'invalid_grant',
  'refresh_token_not_found',
  'session_not_found',
  'user_banned',
]);

/**
 * Whether a `refreshSession()` failure is unrecoverable (the user must
 * re-auth) as opposed to transient (network reset, offline, 5xx - retry
 * later). The SINGLE terminal classifier: the resume pipeline and the auth
 * circuit breaker both decide through this, so the two can never disagree
 * about what kills a session. Matches on `error.code` when the SDK provides
 * one; the message fallback exists only for older GoTrue responses that
 * carry the code solely in the message, and never widens beyond the two
 * legacy invalid-grant spellings.
 */
export function isTerminalAuthRefreshError(error: AuthError | null): boolean {
  if (!error) {
    return false;
  }
  // Only an API response can be terminal: anything else (fetch failures,
  // retryable wrappers, plain Errors) is transport-shaped, and a transport
  // error whose message merely MENTIONS invalid_grant must never sign
  // someone out.
  if (!(error instanceof AuthApiError)) {
    return false;
  }
  if (error.code) {
    return TERMINAL_AUTH_REFRESH_CODES.has(error.code);
  }
  if (error.status !== 400) {
    return false;
  }
  const haystack = getErrorMessage(error).toLowerCase();
  return haystack.includes('invalid_grant') || haystack.includes('refresh_token_not_found');
}

interface ResumeSession {
  access_token: string;
  expires_at?: number;
}

type ResumeSessionResolution =
  | { readonly kind: 'ready'; readonly session: ResumeSession }
  | { readonly kind: typeof RESUME_RUN_NOT_RECOVERED }
  | { readonly kind: typeof RESUME_RUN_RETRYABLE_FAILURE };

/**
 * Resolve the session the resume tail should run on, or classify why recovery
 * did not complete so a queued reconnect can retry only transient failures.
 * Skipping the refresh for a fresh token matters because every rotation risks
 * the lost-response reuse-detection race; the tail still runs regardless, because
 * suspension kills the realtime socket whatever the token's age. A missing
 * expires_at refreshes conservatively.
 */
async function resolveResumedSession(
  deps: RunAppResumeDeps,
  session: ResumeSession,
  now: () => number,
): Promise<ResumeSessionResolution> {
  const expiresAtMs = typeof session.expires_at === 'number' ? session.expires_at * 1000 : null;
  if (expiresAtMs !== null && expiresAtMs - now() > REFRESH_EXPIRY_MARGIN_MS) {
    return { kind: 'ready', session };
  }

  const { data, error } = await deps.supabase.auth.refreshSession();
  if (error) {
    if (isTerminalAuthRefreshError(error)) {
      deps.tripCircuit();
      return { kind: RESUME_RUN_NOT_RECOVERED };
    }
    // Transient/offline: no log, no trip - retry on the next resume.
    return { kind: RESUME_RUN_RETRYABLE_FAILURE };
  }
  // A refresh that reports success but yields no session is anomalous; do not
  // rejoin realtime or refetch on a tokenless client - the exact stale-JWT 401
  // the refresh-before-invalidate ordering exists to prevent.
  return data.session
    ? { kind: 'ready', session: data.session }
    : { kind: RESUME_RUN_NOT_RECOVERED };
}

async function executeResumeRun(
  deps: RunAppResumeDeps,
  now: () => number,
): Promise<ResumeRunOutcome> {
  const {
    data: { session },
  } = await deps.supabase.auth.getSession();
  if (!session) {
    return RESUME_RUN_NOT_RECOVERED;
  }

  const resolution = await resolveResumedSession(deps, session, now);
  if (resolution.kind !== 'ready') {
    return resolution.kind;
  }

  await deps.applyRealtimeAuthToken(resolution.session.access_token);
  deps.dispatchRealtimeRejoin();
  await deps.queryClient.invalidateQueries({ refetchType: 'active' });
  return RESUME_RUN_COMPLETED;
}

function startResumeRun(deps: RunAppResumeDeps, now: () => number): ActiveResumeRun {
  const attempt = executeResumeRun(deps, now);
  const run: ActiveResumeRun = { promise: attempt };
  run.promise = attempt.finally(() => {
    if (activeResumeRun === run) {
      activeResumeRun = null;
    }
  });
  activeResumeRun = run;
  return run;
}

/**
 * Single-owner resume pipeline for a suspended native or desktop app.
 *
 * iOS backgrounding and macOS sleep freeze Supabase's auto-refresh timer and
 * can leave the realtime socket dead. On resume this refreshes the
 * session, pushes the fresh JWT to realtime, signals a channel rejoin, then
 * refetches active queries - IN THAT ORDER.
 *
 * The refresh-BEFORE-invalidate order is load-bearing: invalidating first would
 * refetch on a stale JWT, 401, and falsely trip the circuit breaker.
 */
export async function runAppResume(
  deps: RunAppResumeDeps,
  options: RunAppResumeOptions = {},
): Promise<void> {
  const now = deps.now ?? Date.now;

  if ((!isNativePlatform() && !isDesktopPlatform()) || deps.isAuthCircuitOpen()) {
    return;
  }

  const joinedRun = activeResumeRun;
  if (joinedRun) {
    const outcome = await joinedRun.promise;
    if (
      !options.retryAfterActiveTransientFailure ||
      outcome !== RESUME_RUN_RETRYABLE_FAILURE ||
      deps.isAuthCircuitOpen()
    ) {
      return;
    }

    // Another waiter may already own the retry. Join it rather than rotating
    // the refresh token twice for the same reconnect edge.
    const retryInProgress = activeResumeRun;
    if (retryInProgress) {
      await retryInProgress.promise;
      return;
    }
  }

  // Synchronous time guards and ownership run before the first await so a
  // rapid foreground/background flap cannot launch concurrent refreshes.
  const nowMs = now();
  if (!options.allowDuringDebounce && nowMs - lastRunMs < RESUME_DEBOUNCE_MS) {
    return;
  }
  if (!options.allowDuringColdStart && nowMs - processStartMs < COLD_START_GRACE_MS) {
    return;
  }
  // A logged-out resume harmlessly starts the clock too - there is nothing to
  // do while signed out.
  lastRunMs = nowMs;

  await startResumeRun(deps, now).promise;
}

export async function runNativeResume(deps: RunNativeResumeDeps): Promise<void> {
  await runAppResume(deps);
}

/** Reconnect is new information and must not be suppressed by startup or resume debounce. */
export async function runNativeReconnect(deps: RunNativeResumeDeps): Promise<void> {
  await runAppResume(deps, {
    allowDuringColdStart: true,
    allowDuringDebounce: true,
    retryAfterActiveTransientFailure: true,
  });
}
